import React, { useEffect, useState } from "react";
import { CalendarCheck, CalendarClock, Clock } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { StatCard, EmptyState, StatusBadge } from "@/components/dashboard/ui";

export default function SecretaryHome() {
  const { user } = useAuth();
  const [missions, setMissions] = useState([]);
  const load = () => api.get("/missions").then(({ data }) => setMissions(data || []));
  useEffect(() => { load(); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const weekEnd = new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10);
  const todays = missions.filter((m) => m.date === today && m.status !== "Annulée");
  const week = missions.filter((m) => m.date >= today && m.date <= weekEnd && m.status !== "Annulée");
  const proposed = missions.filter((m) => m.status === "Attribuée");

  const act = async (m, status) => {
    try { await api.put(`/missions/${m.mission_id}`, { status }); toast.success("Mission mise à jour"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900">Bonjour {user?.first_name}</h1>
      <p className="mt-1 text-slate-500 mb-6">Voici votre planning et vos missions.</p>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard icon={CalendarCheck} label="Missions aujourd'hui" value={todays.length} tone="primary" testid="sec-stat-today" />
        <StatCard icon={CalendarClock} label="Cette semaine" value={week.length} tone="accent" />
        <StatCard icon={Clock} label="À confirmer" value={proposed.length} tone="amber" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
        <h3 className="font-heading font-semibold text-slate-900 mb-4">Missions du jour</h3>
        {todays.length === 0 ? <EmptyState icon={CalendarCheck} title="Aucune mission aujourd'hui" /> : (
          <div className="space-y-3">{todays.map((m) => (
            <div key={m.mission_id} className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-slate-50">
              <div><p className="text-sm font-medium text-slate-900">{m.start} – {m.end} · {m.client_name}</p><p className="text-xs text-slate-500">{(m.services || []).join(", ")}</p></div>
              <div className="flex items-center gap-2"><StatusBadge status={m.status} />{m.status !== "Terminée" && <Button size="sm" variant="outline" onClick={() => act(m, "Terminée")} data-testid={`mission-done-${m.mission_id}`}>Terminer</Button>}</div>
            </div>))}
          </div>
        )}
      </div>

      {proposed.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">Missions attribuées à confirmer</h3>
          <div className="space-y-3">{proposed.map((m) => (
            <div key={m.mission_id} className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-amber-50/50 border border-amber-100">
              <div><p className="text-sm font-medium text-slate-900">{new Date(m.date).toLocaleDateString("fr-CH")} · {m.start}–{m.end}</p><p className="text-xs text-slate-500">{m.client_name} · {(m.services || []).join(", ")}</p></div>
              <div className="flex gap-2"><Button size="sm" className="bg-primary" onClick={() => act(m, "Confirmée")} data-testid={`mission-accept-${m.mission_id}`}>Accepter</Button><Button size="sm" variant="outline" className="text-rose-600" onClick={() => act(m, "Annulée")}>Refuser</Button></div>
            </div>))}
          </div>
        </div>
      )}
    </div>
  );
}
